// Inhalte der Einführung (Rundgang + Seiten-Tipps) – reine Daten/Logik, getestet in onboardingContent.test.js.
// Bei großen Neuerungen ONBOARDING_VERSION erhöhen: dann sehen alle Nutzer den Rundgang erneut.

export const ONBOARDING_VERSION = 1;

const list = (labels) => labels.length <= 1 ? labels.join('') : `${labels.slice(0, -1).join(', ')} und ${labels[labels.length - 1]}`;

/**
 * Schritte des Rundgangs. `area`: nur zeigen, wenn der Nutzer diesen Bereich hat.
 * `text` darf eine Funktion der sichtbaren Navigation sein (zeigt nur, was der Nutzer wirklich nutzen kann).
 */
export const TOUR_STEPS = [
  {
    id: 'welcome',
    icon: 'sparkles',
    title: 'Willkommen bei der Bentomax Material-App',
    text: 'In einer Minute zeigen wir dir das Wichtigste. Du kannst jederzeit überspringen – die Einführung findest du später unter Einstellungen und im Profilmenü.'
  },
  {
    id: 'navigation',
    icon: 'layout',
    title: 'Alles in wenigen Bereichen',
    text: (nav) => `Die App ist in ${nav.length} Bereiche gegliedert: ${list(nav.map((a) => a.label))}. Auf dem Handy findest du sie unten am Bildschirm, auf Tablet und PC links.`
  },
  {
    id: 'parts',
    icon: 'search',
    area: 'teile',
    title: 'Teile finden',
    text: 'Unter „Teile“ suchst du nach Bezeichnung oder Artikelnummer oder wählst zuerst ein Gerät aus. Über die Lupe oben (am PC auch mit Strg+K) findest du jede Seite und durchsuchst die ganze App.'
  },
  {
    id: 'order',
    icon: 'cart',
    area: 'bestellen',
    title: 'Bestellen',
    text: (nav) => {
      const labels = nav.find((a) => a.key === 'bestellen')?.items.map((i) => i.label) || [];
      return `Unter „Bestellen“ findest du ${list(labels)}. Lege Teile in den Warenkorb, trage die Ticketnummer ein und sende die Bestellung ab.`;
    }
  },
  {
    id: 'tools',
    icon: 'tools',
    area: 'werkzeuge',
    title: 'Werkzeuge',
    text: (nav) => {
      const labels = nav.find((a) => a.key === 'werkzeuge')?.items.map((i) => i.label) || [];
      return `Unter „Werkzeuge“ findest du ${list(labels)} – alles, was du beim Kunden vor Ort brauchst.`;
    }
  },
  {
    id: 'offline',
    icon: 'wifiOff',
    title: 'Funktioniert auch offline',
    text: 'Die App speichert Teile und Daten auf deinem Gerät. Ohne Netz kannst du weiter suchen; sobald du wieder online bist, wird automatisch synchronisiert. Den Status siehst du oben.'
  },
  {
    id: 'admin',
    icon: 'admin',
    area: 'verwaltung',
    title: 'Verwaltung',
    text: 'Alle Verwaltungsfunktionen, für die du Rechte hast, findest du als Kacheln unter „Verwaltung“. Tippe oben einen Begriff ein, um schnell die richtige Seite zu finden.'
  },
  {
    id: 'done',
    icon: 'check',
    title: 'Los geht’s!',
    text: 'Über dein Profil oben rechts erreichst du Einstellungen, Feedback und diese Einführung. Auf einigen Seiten erscheinen beim ersten Besuch kurze Tipps.'
  }
];

/** Schritte für die sichtbare Navigation, Texte bereits aufgelöst */
export function stepsFor(nav) {
  const areas = new Set(nav.map((a) => a.key));
  return TOUR_STEPS
    .filter((step) => !step.area || areas.has(step.area))
    .map((step) => ({ ...step, text: typeof step.text === 'function' ? step.text(nav) : step.text }));
}

/** Tipps beim ersten Besuch einer Seite (Schlüssel = Pfad) */
export const PAGE_TIPS = {
  '/search': { id: 'search', text: 'Suche nach Bezeichnung oder Artikelnummer. Mehrere Begriffe grenzen die Treffer ein.' },
  '/devices': { id: 'devices', text: 'Wähle zuerst Kunde und Gerät – danach siehst du nur die passenden Ersatzteile.' },
  '/cart': { id: 'cart', text: 'Bei Ersatzteilen für Mettler-Toledo brauchst du die externe Ticketnummer (beginnt mit einer Ziffer). Sie wird schon beim Eintippen geprüft.' },
  '/verwaltung': { id: 'admin-hub', text: 'Tipp: Tippe oben einen Begriff wie „Kunden“ ein, um die passende Verwaltungsseite sofort zu finden.' }
};

/**
 * Einmalige Hinweise nach dem Anmelden – jeder Nutzer sieht jeden Hinweis genau einmal
 * (gemerkt in onboarding.seenNews). Neuer Hinweis = neuer Eintrag mit neuer id.
 * `action`: Knopf unter dem Text (siehe OnboardingActions.jsx).
 */
export const NEWS_STEPS = [
  {
    id: 'news-passkey',
    icon: 'key',
    action: 'passkey',
    title: 'Schneller anmelden mit Passkey',
    text: 'Mit einem Passkey meldest du dich per Fingerabdruck, Gesichtserkennung oder Geräte-PIN an – ohne Passwort einzutippen. Er wird sicher auf deinem Gerät gespeichert. Einrichten oder entfernen kannst du Passkeys jederzeit unter Einstellungen.'
  },
  {
    id: 'news-classic-design',
    icon: 'layout',
    title: 'Lieber zurück zum alten Design?',
    text: 'Du kannst jederzeit unter Einstellungen → Konto → Darstellung zum alten Design wechseln. Bitte beachte: Das alte Design wird nicht mehr weiterentwickelt und nicht mehr unterstützt – neue Funktionen gibt es nur im neuen Design. Es wird voraussichtlich in Zukunft abgeschaltet.'
  }
];

/** Noch nicht gesehene Hinweise */
export const pendingNews = (onboarding) => NEWS_STEPS.filter((step) => !onboarding?.seenNews?.includes(step.id));

/**
 * Was im Fenster gezeigt wird:
 * - Rundgang noch nicht gesehen → Rundgang mit den Hinweisen vor dem letzten Schritt
 * - Rundgang gesehen → nur die noch offenen Hinweise
 * - manuell gestartet (Einstellungen/Profilmenü) → Rundgang mit allen Hinweisen
 */
export function sessionSteps(nav, onboarding, { manual = false } = {}) {
  const news = manual ? NEWS_STEPS : pendingNews(onboarding);
  if (!manual && !needsTour(onboarding)) return news;
  const tour = stepsFor(nav);
  return [...tour.slice(0, -1), ...news, ...tour.slice(-1)];
}

/** Muss der Rundgang (erneut) gezeigt werden? */
export function needsTour(onboarding, version = ONBOARDING_VERSION) {
  return !(onboarding?.done && (onboarding.version ?? 0) >= version);
}
